import { getVdoCipherIframeUrl } from "constants/recordedContent";
import { getFibonacciNthValue } from "features/Common/modules/DataTypes/utils/numbers";
import { VDOCIPHER_VIDEO_STATUS } from "features/MediaLibrary/constants/MediaLibrary.constants";
import { getRetryDelayPromise } from "features/MediaLibrary/utils/MediaLibrary.utils";
import { useEffect, useState } from "react";
import { logError } from "utils/error";
import { is_empty } from "utils/validations";
import {
  getVdoCipherVideoOTP,
  getVdoCipherVideoStatus,
} from "./recordedContentUtils";

/**
 * hook to get VdoCipher playback details
 * TODO: split this hook: create separate hook for video status and playback details
 */
const useVdoCipherVideoDetails = ({
  videoId,
  shouldFetchStatus = true, // Determines whether to fetch the video status. Set to false if the status is already saved in the database.
  shouldFetchPlaybackDetails = true, // Determines whether to fetch playback details. Used only when the video needs to be played on screen.
  // eslint-disable-next-line no-unused-vars
  onFetchStatus = (status) => {},
}) => {
  const [playbackDetails, setPlaybackDetails] = useState();
  const [isLoadingPlaybackDetailsData, setIsLoadingPlaybackDetailsData] =
    useState(false);
  const [videoStatus, setVideoStatus] = useState();
  const [isLoadingVideoStatusData, setIsLoadingVideoStatusData] =
    useState(false);

  const handleFetchPlaybackDetails = async () => {
    if (is_empty(videoId) || isLoadingPlaybackDetailsData) return;

    try {
      setIsLoadingPlaybackDetailsData(true);
      const details = await getVdoCipherVideoOTP(videoId);
      setPlaybackDetails(details);
    } catch (error) {
      logError({
        error,
        when: "calling handleFetchPlaybackDetails",
        occuredAt: "src/utils/recordedContentUtils/useVdoCipherVideoDetails.js",
      });
    } finally {
      setIsLoadingPlaybackDetailsData(false);
    }
  };

  const handleFetchVideoStatus = async (retryCount = 0) => {
    if (is_empty(videoId) || isLoadingVideoStatusData) return;

    try {
      setIsLoadingVideoStatusData(true);
      const status = await getVdoCipherVideoStatus(videoId);

      if (onFetchStatus) onFetchStatus(status);

      setVideoStatus(status);

      if (status.status !== VDOCIPHER_VIDEO_STATUS.READY) {
        const retryDelay = getFibonacciNthValue(retryCount);
        await getRetryDelayPromise(retryDelay);
        handleFetchVideoStatus(retryCount + 1);
      } else if (retryCount) {
        if (shouldFetchPlaybackDetails) handleFetchPlaybackDetails();
      }
    } catch (error) {
      logError({
        error,
        when: "calling handleFetchVideoStatus",
        occuredAt: "src/utils/recordedContentUtils/useVdoCipherVideoDetails.js",
      });
    } finally {
      setIsLoadingVideoStatusData(false);
    }
  };

  const handleFetchVideoDetails = () => {
    if (shouldFetchPlaybackDetails) handleFetchPlaybackDetails();
    if (shouldFetchStatus) handleFetchVideoStatus();
  };

  useEffect(() => {
    handleFetchVideoDetails();
  }, [videoId]);

  return {
    loading: isLoadingPlaybackDetailsData || isLoadingVideoStatusData,
    loaded: !!(playbackDetails && videoStatus),
    videoStatus,
    embedUrl: getVdoCipherIframeUrl(playbackDetails),
    isReady: videoStatus?.status === VDOCIPHER_VIDEO_STATUS.READY,
    refetch: handleFetchVideoDetails,
  };
};

export default useVdoCipherVideoDetails;
